import { _range } from '@naturalcycles/js-lib'
import { localeToRatingTextMap } from '@src/cnst/star-rating.cnst'
import { languageContext } from '@src/context/language.context'
import { Breakpoints } from '@src/hooks/useBreakpoint'
import { useWindowDimensions } from '@src/hooks/useWindowDimensions'
import { DatoCmsFileField } from '@src/typings/dato-cms-assets'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { useContext } from 'react'
import { Button } from '../button/Button.component'
import { Carousel } from '../carousel/Carousel.component'
import { NcImage } from '../nc-image/NcImage.component'
import { Rating } from '../rating/Rating.component'
import styles from './WideReviews.module.scss'

export interface WideReviewsProps {
  heading: string
  reviews: {
    image: DatoCmsFileField
    content: string
    user: string
    ncPlan?: 'NC° Birth Control' | 'NC° Plan Pregnancy'
    ncHardware?: string
    id: string
  }[]
  reviewsCta?: {
    text: string
    url: string
  }
  loading?: 'lazy' | 'eager'
  theme?: 'default' | 'transparentSimple' | 'ab349'
}

export function WideReviews({
  heading,
  reviews,
  reviewsCta,
  loading = 'lazy',
  theme = 'default',
}: WideReviewsProps): ReactNode {
  const { locale } = useContext(languageContext)
  const { width } = useWindowDimensions()
  const isMobile = !width || width < Breakpoints.bpMedium

  return (
    <section className={classNames(styles.WideReviews, styles[`WideReviews___${theme}`])}>
      <h3 className={styles.WideReviews__heading}>{heading}</h3>
      {theme === 'default' && (
        <div className={styles.WideReviews__rating}>
          <Rating appDistributor="App Store" reviews={localeToRatingTextMap[locale]} />
        </div>
      )}

      <Carousel
        className={'Carousel___mediumDesktopGap'}
        enableOnAllSizes={true}
        isActive={isMobile}
      >
        {reviews.map(review => (
          <div key={review.content} className={styles.WideReviews__card}>
            <p className={styles.WideReviews__user}>{review.user}</p>
            <NcImage {...review.image} className={styles.WideReviews__image} loading={loading} />
            <p className={styles.WideReviews__content}>{review.content}</p>
            <div className={styles.WideReviews__endSection}>
              {review.ncPlan && (
                <img
                  className={styles.WideReviews__plan}
                  alt={review.ncPlan}
                  src={
                    review.ncPlan === 'NC° Birth Control'
                      ? '/icons/logo-thin-birth-control.svg'
                      : '/icons/logo-thin-plan-pregnancy.svg'
                  }
                />
              )}

              {review.ncHardware && (
                <div
                  className={styles.WideReviews__hardware}
                  dangerouslySetInnerHTML={{ __html: review.ncHardware }}
                />
              )}

              <div aria-label="5 stars">
                {_range(5).map(i => {
                  return <img key={i} alt="star" aria-hidden="true" src="/icons/small-star.svg" />
                })}
              </div>
            </div>
          </div>
        ))}
      </Carousel>

      {reviewsCta && (
        <div className={styles.WideReviews__cta}>
          <Button theme={'ghost'} href={reviewsCta.url}>
            {reviewsCta.text}
          </Button>
        </div>
      )}
    </section>
  )
}
